import { useEffect, useMemo, useState } from "react";
import {
  RiAlertLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiFolderOpenFill,
  RiFileCopyLine,
  RiGalleryLine,
  RiNodeTree,
  RiListSettingsLine,
  RiSearchLine,
  RiFolder3Fill,
} from "react-icons/ri";
import { DirectionCode, PhotoDraft, PhotoSection, productKeyOf } from "./photoModel";
import { ProductInfo } from "./useProductValidation";
import { ItemState } from "./useUploadQueue";
import ProductGroup, { ProductGroupData } from "./ProductGroup";
import PhotoTree from "./PhotoTree";
import { BoardFilter, buildTree, filterTree, uploadKeyOf } from "./treeModel";
import { useTreeExpansion } from "./useTreeExpansion";
import { Chip } from "./TreeChips";
import TreeRowMenu, { RowMenuItem } from "./TreeRowMenu";

export type BoardView = "tree" | "grid";

export interface BarcodeBoardProps {
  drafts: PhotoDraft[];
  productInfo: Record<string, ProductInfo>;
  fileNames: Record<string, string>;
  conflicts: Set<string>;
  selected: Set<string>;
  uploads: Record<string, ItemState>;
  /** Sections emptied by a move, kept on screen because they existed in the drop. */
  ghostSections?: Set<string>;
  disabled?: boolean;
  /** Bumped by the caller after an upload run that left photos behind; switching this
   *  value moves the board onto the "ยังไม่ขึ้น" lens so the leftovers are what is on screen. */
  pendingSignal?: number;
  view: BoardView;
  onViewChange(v: BoardView): void;
  onToggleSelect(id: string): void;
  onSetSelection(ids: string[], on: boolean): void;
  onOpen(id: string): void;
  onSetDirection(id: string, d: DirectionCode): void;
  onMoveSection(ids: string[], s: PhotoSection): void;
  onSetMain(id: string): void;
  onDeleteMany(ids: string[]): void;
  onDismissSection?(id: string): void;
  onRetry?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDeleteGroup?: (productKey: string) => void;
}

export default function BarcodeBoard({
  drafts,
  productInfo,
  fileNames,
  conflicts,
  selected,
  uploads,
  ghostSections,
  disabled,
  pendingSignal,
  view,
  onViewChange,
  onToggleSelect,
  onSetSelection,
  onOpen,
  onSetDirection,
  onMoveSection,
  onSetMain,
  onDeleteMany,
  onDismissSection,
  onRetry,
  onDelete,
  onDeleteGroup,
}: BarcodeBoardProps) {
  const [filter, setFilter] = useState<BoardFilter>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (pendingSignal) setFilter("pending");
  }, [pendingSignal]);

  const tree = useMemo(
    () => buildTree({ drafts, productInfo, fileNames, conflicts, uploads, ghostSections }),
    [drafts, productInfo, fileNames, conflicts, uploads, ghostSections]
  );
  // Expansion tracks the whole tree, so filtering never silently reopens or closes a folder.
  const { expanded, toggle, setOpen, expandAll, collapseAll, allOpen } = useTreeExpansion(tree);
  const visibleTree = useMemo(() => filterTree(tree, filter, query), [tree, filter, query]);

  // The summary describes what is on screen: narrow with a lens or the search box and the
  // chips follow, so the numbers and the rows below them can never disagree.
  const counts = useMemo(() => {
    const total = visibleTree.reduce(
      (acc, b) => {
        acc.photos += b.stats.photos;
        acc.unknown += b.stats.unknown;
        acc.dup += b.stats.dup;
        acc.invalid += b.stats.invalid;
        return acc;
      },
      { photos: 0, unknown: 0, dup: 0, invalid: 0 }
    );
    return {
      ...total,
      barcodes: visibleTree.length,
      products: visibleTree.reduce((n, b) => n + b.matcodes.length, 0),
    };
  }, [visibleTree]);

  // "Everything is ready" is a claim about photos, so an empty result never makes it.
  const allClear =
    counts.photos > 0 && counts.unknown === 0 && counts.invalid === 0 && counts.dup === 0;

  // DESIGN.md's Segmented Filter pairs every lens with a live count, so the uploader can
  // see what a lens holds before switching into it.
  const lensCounts = useMemo(() => {
    const mats = tree.flatMap((b) => b.matcodes);
    return {
      all: mats.length,
      todo: mats.filter((m) => m.stats.unknown > 0 || m.stats.invalid > 0).length,
      dup: mats.filter((m) => m.stats.dup > 0).length,
      // Counted in photos, because this lens narrows to photos rather than to matcodes.
      pending: tree.reduce((n, b) => n + b.stats.photos - b.stats.success, 0),
    };
  }, [tree]);

  // Only meaningful once something has landed; before that every photo is "pending".
  const anyLanded = useMemo(
    () => Object.values(uploads).some((u) => u.status === "success"),
    [uploads]
  );

  // Grid mode keeps the shipped card layout untouched; it reads the same filtered tree.
  const groups: ProductGroupData[] = useMemo(
    () =>
      visibleTree.flatMap((b) =>
        b.matcodes.map((m) => ({
          productKey: m.id,
          barcode: m.barcode,
          matCode: m.matCode,
          drafts: m.sections.flatMap((s) => s.files.map((f) => f.draft)),
        }))
      ),
    [visibleTree]
  );

  /** `short` is the label a phone-width slice of the control can actually hold. */
  const seg = (f: BoardFilter, label: string, unit: string, short?: string) => {
    const n = lensCounts[f];
    const active = filter === f;
    return (
      <button
        type="button"
        onClick={() => setFilter(f)}
        aria-pressed={active}
        // Spelled out, because the visual label and its count are separate flex items and
        // would otherwise be announced run together as "ต้องแก้(1 matcode)".
        aria-label={`${label} (${n} ${unit})`}
        disabled={n === 0 && !active}
        className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 disabled:cursor-not-allowed disabled:text-gray-300 sm:flex-none sm:justify-start ${
          active ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100 disabled:hover:bg-transparent"
        }`}
      >
        {short ? (
          <>
            <span className="sm:hidden">{short}</span>
            <span className="hidden sm:inline">{label}</span>
          </>
        ) : (
          label
        )}
        <span className={`tabular-nums ${active ? "text-blue-100" : "text-gray-400"}`}>
          ({n}
          <span className="hidden sm:inline"> {unit}</span>)
        </span>
      </button>
    );
  };

  // Folding and layout are set-once controls: rare enough that a reserved row of chrome
  // costs more than it returns, common enough that they must stay one predictable click
  // away. One overflow button holds both, and the summary row keeps only what is in use.
  const boardMenu: RowMenuItem[] = [
    {
      label: allOpen ? "ยุบทั้งหมด" : "กางทั้งหมด",
      icon: allOpen ? <RiFolder3Fill size={14} /> : <RiFolderOpenFill size={14} />,
      onSelect: allOpen ? collapseAll : expandAll,
      disabled: view !== "tree",
    },
    {
      label: "มุมมองแบบ Tree",
      icon: <RiNodeTree size={14} />,
      onSelect: () => onViewChange("tree"),
      trailing: view === "tree" ? <RiCheckLine size={14} aria-label="เลือกอยู่" /> : undefined,
    },
    {
      label: "มุมมองแบบ Grid",
      icon: <RiGalleryLine size={14} />,
      onSelect: () => onViewChange("grid"),
      trailing: view === "grid" ? <RiCheckLine size={14} aria-label="เลือกอยู่" /> : undefined,
    },
  ];

  return (
    <div>
      {/* Search takes the line under the drop zone, and the board's own options ride along
          on its spare width — that keeps the summary row below to one line even when every
          chip and every lens is showing. */}
      <div className="flex items-center gap-2 py-2">
        <div className="relative min-w-0 flex-1">
          <RiSearchLine
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            aria-label="ค้นหา barcode / matcode"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหา barcode / matcode"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm leading-6 text-gray-700 transition-colors placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
          />
        </div>
        <TreeRowMenu
          items={boardMenu}
          label="ตัวเลือกมุมมอง"
          icon={<RiListSettingsLine size={17} aria-hidden="true" />}
          triggerClassName="h-[38px] w-[38px] rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        />
      </div>

      {/* Sticky summary + controls — separated from what scrolls under it by a rule and a
          blur. No side padding: the chips and the lenses sit on the same left and right
          edges as the search field above and the tree card below, so the column reads as
          one stack of aligned edges rather than three boxes of different widths. */}
      <div className="static border-b border-gray-200 bg-white/95 py-2 backdrop-blur sm:sticky sm:top-0 sm:z-30">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          {/* Named as a group: these numbers describe the visible set, not the whole batch. */}
          <div
            role="group"
            aria-label="สรุปสิ่งที่แสดงอยู่"
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5"
          >
            <Chip tone="count">{counts.barcodes} barcode</Chip>
            <Chip tone="count">{counts.products} matcode</Chip>
            <Chip tone="info">{counts.photos} รูป</Chip>

            {counts.unknown > 0 && (
              <Chip tone="warn" icon={<RiAlertLine size={13} aria-hidden="true" />}>
                {counts.unknown} ยังไม่รู้มุม
              </Chip>
            )}
            {counts.invalid > 0 && (
              <Chip tone="error" icon={<RiCloseCircleLine size={13} aria-hidden="true" />}>
                {counts.invalid} ไม่พบสินค้า
              </Chip>
            )}
            {counts.dup > 0 && (
              <Chip tone="dup" icon={<RiFileCopyLine size={13} aria-hidden="true" />}>
                {counts.dup} ซ้ำของเดิม
              </Chip>
            )}
            {allClear && (
              <Chip tone="ready" icon={<RiCheckLine size={13} aria-hidden="true" />}>
                ทุกรูปพร้อมอัปโหลด
              </Chip>
            )}
          </div>

          <div className="flex w-full overflow-hidden rounded-lg border border-gray-300 divide-x divide-gray-300 sm:ml-auto sm:w-auto">
            {seg("all", "ทั้งหมด", "matcode")}
            {seg("todo", "ต้องแก้", "matcode")}
            {seg("dup", "ซ้ำของเดิม", "matcode", "ซ้ำ")}
            {(anyLanded || filter === "pending") &&
              seg("pending", "ยังไม่ขึ้น", "รูป", "เหลือ")}
          </div>
        </div>
      </div>

      <div className="mt-3">
        {visibleTree.length === 0 ? (
          <p className="py-8 text-center text-sm leading-6 text-gray-400">
            {filter === "pending"
              ? "ทุกรูปขึ้นครบแล้ว"
              : "ไม่มีกลุ่มที่ตรงกับตัวกรองนี้"}
          </p>
        ) : view === "tree" ? (
          <PhotoTree
            tree={visibleTree}
            fileNames={fileNames}
            selected={selected}
            expanded={expanded}
            disabled={disabled}
            onToggleExpand={toggle}
            onSetExpanded={setOpen}
            onSetSelection={onSetSelection}
            onToggleSelect={onToggleSelect}
            onOpen={onOpen}
            onSetDirection={onSetDirection}
            onMoveSection={onMoveSection}
            onSetMain={onSetMain}
            onDeleteMany={onDeleteMany}
            onDelete={onDelete}
            onRetry={onRetry}
            onDismissSection={onDismissSection}
          />
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
            {groups.map((g) => (
              <ProductGroup
                key={g.productKey}
                group={g}
                info={productInfo[productKeyOf(g.barcode, g.matCode)]}
                fileNames={fileNames}
                conflicts={conflicts}
                selected={selected}
                uploads={uploads}
                onToggleSelect={onToggleSelect}
                onOpen={onOpen}
                onRetry={onRetry}
                onDelete={onDelete}
                onDeleteGroup={onDeleteGroup}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { uploadKeyOf };
